// hooks/useBranding.ts

import { useCallback } from "react";
import { useStore } from "../store/store";
import { brandingService } from "../api/branding/branding.service";
import { apiURL, applyRootStyles, setFavicon } from "../utils";
import { useTheme } from "@/components/ui/ThemeToggler";
import { useBasicFlags } from "./useBasicFlags";

export const useBranding = () => {
  const setIsBrandingLoading = useStore((state) => state.setIsBrandingLoading);
  const isBrandingLoading = useStore((state) => state.isBrandingLoading);
  const setBrandingList = useStore((state) => state.setBrandingList);
  const setIsBrandingCreated = useStore((state) => state.setIsBrandingCreated);
  const brandingList = useStore((state) => state.brandingList);
  const { entity } = useBasicFlags();
  const { theme } = useTheme();

  const fetchBranding = useCallback(
    async (explicitEntityId?: string) => {
      const entity_id = explicitEntityId ?? entity?.internal_id;
      if (!entity_id) return;

      try {
        setIsBrandingLoading(true);
        const brandingResult = await brandingService.getBrandingList({
          searchParams: { entity_id },
        });

        if (
          brandingResult?.data &&
          Object.keys(brandingResult.data ?? {}).length > 0
        ) {
          const brandingData = {
            ...brandingResult.data,
            primary_color_light:
              brandingResult.data.primary_color_light ??
              brandingList?.primary_color_light,
            primary_color_dark:
              brandingResult.data.primary_color_dark ??
              brandingList?.primary_color_dark,
            secondary_color_light:
              brandingResult.data.secondary_color_light ??
              brandingList?.secondary_color_light,
            secondary_color_dark:
              brandingResult.data.secondary_color_dark ??
              brandingList?.secondary_color_dark,
          };
          setFavicon(
            theme === "dark"
              ? `${apiURL(brandingResult?.data?.favicon_dark as string).toString() ?? ""}`
              : `${apiURL(brandingResult?.data?.favicon_light as string).toString() ?? ""}`,
          );

          setIsBrandingCreated(true);
          setBrandingList(brandingData);
          applyRootStyles(
            "--primary",
            theme,
            brandingData.primary_color_light,
            brandingData.primary_color_dark ?? "",
          );
          applyRootStyles(
            "--secondary",
            theme,
            brandingData.secondary_color_light,
            brandingData.secondary_color_dark ?? "",
          );
        }
      } catch (error) {
        console.error("Error fetching branding data:", error);
      } finally {
        setIsBrandingLoading(false);
      }
    },
    [
      entity?.internal_id,
      setIsBrandingLoading,
      brandingList?.primary_color_light,
      brandingList?.primary_color_dark,
      brandingList?.secondary_color_light,
      brandingList?.secondary_color_dark,
      theme,
      setIsBrandingCreated,
      setBrandingList,
    ],
  );

  return { fetchBranding, isBrandingLoading };
};
